// src/components/howItWorks.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import "../sass/landing/day.scss";
import Shot from "./landing/Shot";
import { IMAGES } from "./landing/images";
import { scrollToSection } from "./landing/scroll";

const DAY_START = 7 * 60;
const DAY_END = 19 * 60;

const MOMENTS = [
  {
    time: "08:10",
    title: "Você abre o dia e vê seus atendimentos.",
    text: "Quem vem, em que horário e quem já confirmou. Antes do primeiro paciente, você sabe como o dia vai ser.",
    image: IMAGES.celularHoje,
    alt: "PsyAssist no celular mostrando a rotina de hoje, com o próximo atendimento às 08:30 e a confirmação da paciente.",
  },
  {
    time: "10:30",
    title: "Confirmações e agenda já estão organizadas.",
    text: "Enquanto você atende, os lembretes saem no horário combinado e as respostas dos pacientes voltam para a agenda.",
    image: IMAGES.celularAgenda,
    alt: "Agenda do PsyAssist no celular com a sessão marcada como presença confirmada pelo WhatsApp.",
  },
  {
    time: "14:40",
    title: "Ao iniciar o atendimento, o contexto do paciente está disponível.",
    text: "A última evolução, a anamnese e o histórico ficam a um toque. Você começa a sessão lembrando de onde partir.",
    image: IMAGES.celularEvolucao,
    alt: "Evolução mais recente de uma paciente aberta no celular, com o resumo clínico da última sessão.",
  },
  {
    time: "18:20",
    title: "Depois da sessão, financeiro e cobrança continuam organizados.",
    text: "O pagamento entra no financeiro e, se algo ficou pendente, a cobrança segue a sequência que você definiu. Você não leva isso para casa.",
    image: IMAGES.celularCobranca,
    alt: "Tela de cobranças do PsyAssist no celular, com um pagamento pendente, a etapa da régua e a data do próximo envio.",
  },
];

const toPercent = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return ((hours * 60 + minutes - DAY_START) / (DAY_END - DAY_START)) * 100;
};

const HowItWorks = () => {
  const [active, setActive] = useState(0);
  const sectionRef = useRef(null);
  const itemsRef = useRef([]);

  // Usa a distância até uma linha de leitura fixa: evita que dois IntersectionObservers
  // disputem o momento ativo, especialmente em telas com alturas diferentes.
  useEffect(() => {
    let frame = null;

    const updateActive = () => {
      frame = null;
      const section = sectionRef.current;
      if (!section || itemsRef.current.length === 0) return;

      const bounds = section.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;

      const readingLine = Math.min(window.innerHeight * 0.48, window.innerHeight - 80);
      let bestIndex = 0;
      let bestDistance = Infinity;

      itemsRef.current.forEach((element, index) => {
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const middle = rect.top + rect.height / 2;
        const distance = Math.abs(middle - readingLine);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      setActive((previous) => (previous === bestIndex ? previous : bestIndex));
    };

    const scheduleUpdate = () => {
      if (frame === null) frame = window.requestAnimationFrame(updateActive);
    };

    scheduleUpdate();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  const goToMoment = useCallback((index) => {
    const element = itemsRef.current[index];
    if (!element) return;
    setActive(index);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    element.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
  }, []);

  const progress = toPercent(MOMENTS[active].time);

  return (
    <section
      id="como-funciona"
      ref={sectionRef}
      className="lp-day"
      aria-labelledby="day-title"
    >
      <div className="lp-shell">
        <header className="lp-day__header">
          <span className="lp-day__eyebrow">Uma rotina mais leve, do começo ao fim</span>
          <h2 id="day-title">Um dia com o PsyAssist.</h2>
          <p className="lp-day__statement">
            Seu jeito de atender continua o mesmo. A rotina ao redor é que fica
            mais leve.
          </p>
        </header>

        <nav className="lp-day__ruler" aria-label="Momentos de um dia com o PsyAssist">
          <span className="lp-day__ruler-edge" aria-hidden="true">07h</span>
          <div className="lp-day__ruler-track">
            <span
              className="lp-day__ruler-progress"
              style={{ "--day-progress": `${progress}%` }}
              aria-hidden="true"
            />
            {MOMENTS.map((moment, index) => (
              <button
                key={moment.time}
                type="button"
                className={`lp-day__mark${index <= active ? " is-past" : ""}${index === active ? " is-active" : ""}`}
                style={{ left: `${toPercent(moment.time)}%` }}
                aria-label={`Ir para ${moment.time}: ${moment.title}`}
                aria-current={index === active ? "step" : undefined}
                onClick={() => goToMoment(index)}
              >
                <span>{moment.time}</span>
              </button>
            ))}
          </div>
          <span className="lp-day__ruler-edge" aria-hidden="true">19h</span>
        </nav>

        <div className="lp-day__layout">
          <div className="lp-day__device" aria-hidden="true">
            <div className="lp-day__phone">
              <div className="lp-day__phone-camera" />
              {MOMENTS.map((moment, index) => (
                <Shot
                  key={moment.time}
                  image={moment.image}
                  alt=""
                  sizes="320px"
                  className={`lp-day__screen${index === active ? " is-active" : ""}`}
                />
              ))}
            </div>
            <p className="lp-day__device-note">Um dia inteiro, com menos coisas para lembrar.</p>
          </div>

          <ol className="lp-day__moments">
            {MOMENTS.map((moment, index) => (
              <li
                key={moment.time}
                ref={(element) => { itemsRef.current[index] = element; }}
                className={`lp-day__moment${index === active ? " is-active" : ""}`}
              >
                <time className="lp-day__time" dateTime={moment.time}>
                  {moment.time}
                </time>
                <div className="lp-day__text">
                  <span className="lp-day__step-label">Momento {String(index + 1).padStart(2, "0")} / 04</span>
                  <h3>{moment.title}</h3>
                  <p>{moment.text}</p>
                </div>
                <Shot
                  image={moment.image}
                  alt={moment.alt}
                  sizes="(min-width: 720px) 320px, 78vw"
                  className="lp-day__inline"
                />
              </li>
            ))}
          </ol>
        </div>

        <footer className="lp-day__closing">
          <p>
            No fim do dia, sobra menos coisa para lembrar. E mais atenção para
            quem está do outro lado.
          </p>
          <div className="lp-day__actions">
            <button
              type="button"
              className="lp-button lp-button--primary"
              onClick={() => scrollToSection("planos")}
            >
              Começar meus 7 dias grátis
              <ArrowRight size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="lp-link"
              onClick={() => scrollToSection("planos")}
            >
              Comparar os planos
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default HowItWorks;
